<script lang="ts" setup name="Editor2">
import { PropType, computed, unref, shallowRef, ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { i18nChangeLanguage, IDomEditor, IEditorConfig } from '@wangeditor/editor'
import { propTypes } from '@/utils/propTypes'
import { isNumber } from '@/utils/is'
import { ElMessage } from 'element-plus'
import { useLocaleStore } from '@/store/modules/locale'
import { useUserStore } from '@/store/modules/user'
import { SUCCESS_CODE } from '@/constants'
import { ImageElement, InsertFnType1, InsertFnType2, VideoElement } from '../type'
import { bachDelFile } from '@/api/file'

defineOptions({
  name: 'Editor2'
})

const userStore = useUserStore()

// type InsertFnType = (url: string, alt: string, href: string) => void

const localeStore = useLocaleStore()

const currentLocale = computed(() => localeStore.getCurrentLocale)

i18nChangeLanguage(unref(currentLocale).lang)

const props = defineProps({
  editorId: propTypes.string.def('wangeEditor-1'),
  height: propTypes.oneOfType([Number, String]).def('500px'),
  editorConfig: {
    type: Object as PropType<Partial<IEditorConfig>>,
    default: () => undefined
  },
  readonly: propTypes.bool.def(false),
  modelValue: propTypes.string.def('')
})

const emit = defineEmits(['change', 'update:modelValue'])

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef<IDomEditor>()

const valueHtml = ref('')

watch(
  () => props.modelValue,
  (val: string) => {
    if (!val) {
      val = ''
    }
    if (val === unref(valueHtml)) return
    valueHtml.value = val
  },
  {
    immediate: true
  }
)

// 监听
watch(
  () => valueHtml.value,
  (val: string) => {
    emit('update:modelValue', val)
  }
)

const handleCreated = (editor: IDomEditor) => {
  editorRef.value = editor
}

const insertedImageList = ref<any[]>([])
const insertedVideoList = ref<any[]>([])

// 自定义校验图片
const customCheckImageFn = (src: string): boolean | undefined | string => {
  console.log(src)
  if (!src) {
    return
  }
  if (src.indexOf('http') !== 0) {
    console.log(src)
    return '图片网址必须以 http/https 开头'
  }
  return true

  // 返回值有三种选择：
  // 1. 返回 true ，说明检查通过，编辑器将正常插入图片
  // 2. 返回一个字符串，说明检查未通过，编辑器会阻止插入。会 alert 出错误信息（即返回的字符串）
  // 3. 返回 undefined（即没有任何返回），说明检查未通过，编辑器会阻止插入。但不会提示任何信息
}

// 转换图片链接
const customParseImageSrc = (src: string): string => {
  if (src.indexOf('http') !== 0) {
    return `http://${src}`
  }
  return src
}

// 自定义校验视频
const customCheckVideoFn = (src: string): boolean | string | undefined => {
  if (!src) {
    return
  }
  if (src.indexOf('http') !== 0) {
    return '视频地址必须以 http/https 开头'
  }
  return true

  // 返回值有三种选择：
  // 1. 返回 true ，说明检查通过，编辑器将正常插入视频
  // 2. 返回一个字符串，说明检查未通过，编辑器会阻止插入。会 alert 出错误信息（即返回的字符串）
  // 3. 返回 undefined（即没有任何返回），说明检查未通过，编辑器会阻止插入。但不会提示任何信息
}

// 自定义转换视频
const customParseVideoSrc = (src: string): string => {
  if (src.includes('.bilibili.com')) {
    // 转换 bilibili url 为 iframe （仅作为示例，不保证代码正确和完整）
    const arr = src.split('/')
    const vid = arr[arr.length - 1]
    return `<iframe src="//player.bilibili.com/player.html?bvid=${vid}" scrolling="no" border="0" frameborder="no" framespacing="0" allowfullscreen="true"> </iframe>`
  }
  if (src.includes('v.qq.com')) {
    const arr = src.split('/')
    const vid = arr[arr.length - 1]
    return `<iframe frameborder="0" src="https://v.qq.com/txp/iframe/player.html?vid=${vid}" allowFullScreen="true"></iframe>`
  }
  if (src.includes('.youku.com')) {
    const arr = src.split('/')
    const vid = arr[arr.length - 1]
    return `<iframe height=498 width=510 src="https://player.youku.com/embed/${vid}" frameborder=0 allowfullscreen></iframe>`
  }

  if (src.includes('.youtube.com')) {
    const arr = src.split('/')
    const vid = arr[arr.length - 1]
    return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${vid}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }

  if (src.indexOf('http') !== 0) {
    return `http://${src}`
  }
  return src
}

// 自定义校验链接
const customCheckLinkFn = (_: string, url: string): string | boolean | undefined => {
  // TS 语法
  // function customCheckLinkFn(text, url) {                                              // JS 语法

  if (!url) {
    return
  }
  if (url.indexOf('http') !== 0) {
    return '链接必须以 http/https 开头'
  }
  return true

  // 返回值有三种选择：
  // 1. 返回 true ，说明检查通过，编辑器将正常插入链接
  // 2. 返回一个字符串，说明检查未通过，编辑器会阻止插入。会 alert 出错误信息（即返回的字符串）
  // 3. 返回 undefined（即没有任何返回），说明检查未通过，编辑器会阻止插入。但不会提示任何信息
}

// 自定义转换链接 url
const customParseLinkUrl = (url: string): string => {
  // TS 语法
  // function customParseLinkUrl(url) {                // JS 语法

  if (url.indexOf('http') !== 0) {
    return `http://${url}`
  }
  return url
}

// 编辑器配置
const editorConfig = computed((): IEditorConfig => {
  return Object.assign(
    {
      placeholder: '请输入内容...',
      readOnly: props.readonly,
      customAlert: (s: string, t: string) => {
        switch (t) {
          case 'success':
            ElMessage.success(s)
            break
          case 'info':
            ElMessage.info(s)
            break
          case 'warning':
            ElMessage.warning(s)
            break
          case 'error':
            ElMessage.error(s)
            break
          default:
            ElMessage.info(s)
            break
        }
      },
      autoFocus: false,
      scroll: true,
      MENU_CONF: {
        insertImage: {
          onInsertedImage(imageNode: ImageElement | null) {
            if (!imageNode) return
            if (!unref(insertedImageList).some((item) => item.src === imageNode.src)) {
              insertedImageList.value.push(imageNode)
            }
          },
          checkImage: customCheckImageFn, // 也支持 async 函数
          parseImageSrc: customParseImageSrc // 也支持 async 函数
        },
        editImage: {
          onUpdatedImage(imageNode: ImageElement | null) {
            if (!imageNode) return
            const { src, alt, url } = imageNode
            console.log(imageNode)
            console.log('updated image', src, alt, url)
          },
          checkImage: customCheckImageFn, // 也支持 async 函数
          parseImageSrc: customParseImageSrc // 也支持 async 函数
        },
        uploadImage: {
          server: `${import.meta.env.VITE_API_SERVER_PATH}/common/upload`,
          fieldName: 'file',
          headers: {
            [userStore.getTokenKey ?? 'Authorization']: userStore.getToken ?? ''
          },
          // 自定义插入图片
          customInsert(res: any, insertFn: InsertFnType1) {
            // 从 res 中找到 url alt href ，然后插入图片
            if (res?.code !== SUCCESS_CODE) return
            const { key, fileName } = res
            const url = `${import.meta.env.VITE_FILE_URL_PREFIX}${key}`
            insertFn(url, fileName, url)
            if (!unref(insertedImageList).some((item) => item.src === url)) {
              insertedImageList.value.push({ src: url })
            }
          },
          // 单个文件上传失败
          onFailed(file: File, res: any) {
            console.log(`${file.name} 上传失败`, res)
            ElMessage.error(`${file.name} 上传失败`)
          },
          // 上传错误，或者触发 timeout 超时
          onError(file: File, err: any) {
            console.log(`${file.name} 上传出错`, err)
            ElMessage.error(`${err}`)
          }
        },
        insertVideo: {
          onInsertedVideo(videoNode: VideoElement | null) {
            if (!videoNode) return
            if (!unref(insertedVideoList).some((item) => item.src === videoNode.src)) {
              insertedVideoList.value.push(videoNode)
            }
          },
          checkVideo: customCheckVideoFn, // 也支持 async 函数
          parseVideoSrc: customParseVideoSrc // 也支持 async 函数
        },
        uploadVideo: {
          server: `${import.meta.env.VITE_API_SERVER_PATH}/common/upload`,
          fieldName: 'file',
          headers: {
            [userStore.getTokenKey ?? 'Authorization']: userStore.getToken ?? ''
          },
          // 自定义插入视频
          customInsert(res: any, insertFn: InsertFnType2) {
            // 从 res 中找到 url poster ，然后插入视频
            if (res?.code !== SUCCESS_CODE) return
            const { key } = res
            const url = `${import.meta.env.VITE_FILE_URL_PREFIX}${key}`
            insertFn(url, '')
            if (!unref(insertedVideoList).some((item) => item.src === url)) {
              insertedVideoList.value.push({ src: url })
            }
          },
          // 单个文件上传失败
          onFailed(file: File, res: any) {
            console.log(`${file.name} 上传失败`, res)
            ElMessage.error(`${file.name} 上传失败`)
          },
          // 上传错误，或者触发 timeout 超时
          onError(file: File, err: any) {
            console.log(`${file.name} 上传出错`, err)
            ElMessage.error(`${err}`)
          }
        },
        insertLink: {
          checkLink: customCheckLinkFn, // 也支持 async 函数
          parseLinkUrl: customParseLinkUrl // 也支持 async 函数
        },
        editLink: {
          checkLink: customCheckLinkFn, // 也支持 async 函数
          parseLinkUrl: customParseLinkUrl // 也支持 async 函数
        }
      },
      uploadImgShowBase64: true
    },
    props.editorConfig || {}
  )
})

const editorStyle = computed(() => {
  return {
    height: isNumber(props.height) ? `${props.height}px` : props.height
  }
})

// 回调函数
const handleChange = (editor: IDomEditor) => {
  emit('change', editor)
}

// 组件销毁时，及时销毁编辑器
onBeforeUnmount(async () => {
  const editor = unref(editorRef.value)

  const imageList = editor?.getElemsByType('image')
  const videoList = editor?.getElemsByType('video')
  // 过滤掉数组insertedImageList里面的src跟imageList里面的src相同的
  const filteredImageList = unref(insertedImageList)
    ?.filter((item) => !imageList?.some((img: any) => img.src === item.src))
    .map((item) => item.src)
  const filteredVideoList = unref(insertedVideoList)
    ?.filter((item) => !videoList?.some((img: any) => img.src === item.src))
    .map((item) => item.src)
  const delFileList = [...filteredImageList, ...filteredVideoList]
  // 批量删除文件
  if (delFileList.length > 0) {
    await bachDelFile(delFileList.map((item) => item?.substring(item?.lastIndexOf('.com') + 4)))
  }
  insertedImageList.value = []
  insertedVideoList.value = []

  // 销毁，并移除 editor
  editor?.destroy()
})

const getEditorRef = async (): Promise<IDomEditor> => {
  await nextTick()
  return unref(editorRef.value) as IDomEditor
}

defineExpose({
  getEditorRef
})
</script>

<template>
  <div class="border-1 border-solid border-[var(--tags-view-border-color)] z-10">
    <!-- 工具栏 -->
    <Toolbar
      :editor="editorRef"
      :editorId="editorId"
      class="border-0 b-b-1 border-solid border-[var(--tags-view-border-color)]"
    />
    <!-- 编辑器 -->
    <Editor
      v-model="valueHtml"
      :defaultConfig="editorConfig"
      :editorId="editorId"
      :style="editorStyle"
      @on-change="handleChange"
      @on-created="handleCreated"
    />
  </div>
</template>

<style src="@wangeditor/editor/dist/css/style.css"></style>
